import {
  Alert,
  Button,
  Group,
  Paper,
  Radio,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { IconCheck, IconTag } from "@tabler/icons-react";
import PriceCard from "../../../components/PriceCard/PriceCard";
import CoverPlanOptions from "./CoverPlanOptions";
import classes from "./SingleIssuanceWizard.module.css";

const CouponBlock = ({
  couponCode,
  setCouponCode,
  applyCoupon,
  isCouponApplying,
  isCouponApplied,
}) => (
  <Paper withBorder p="md" radius="md" mt="lg">
    <Group gap="xs" mb="sm">
      <IconTag size={16} color="#003594" />
      <Text size="sm" fw={500}>
        Have a coupon code?
      </Text>
    </Group>
    <Group align="flex-end" gap="sm" wrap="wrap">
      <TextInput
        maw={280}
        w={{ base: "100%", xs: 280 }}
        placeholder="Enter coupon code"
        value={couponCode}
        onChange={(e) => setCouponCode(e.target.value)}
      />
      <Button
        variant="outline"
        color="brand"
        onClick={applyCoupon}
        loading={isCouponApplying}
        disabled={isCouponApplying || isCouponApplied}
      >
        Apply
      </Button>
    </Group>
  </Paper>
);

const PriceSummary = ({ sym, originalAmount, discountedAmount, isCouponApplied }) => {
  const total = originalAmount - discountedAmount;
  return (
    <>
      <Paper withBorder p="md" radius="md" mt="md" className={classes.priceBar}>
        <SimpleGrid cols={3}>
          <Stack gap={2} align="center">
            <Text size="xs" c="dimmed">
              Original Amount
            </Text>
            <Text fw={600}>
              {sym} {Number(originalAmount).toLocaleString("en-IN")}
            </Text>
          </Stack>
          <Stack gap={2} align="center">
            <Text size="xs" c="dimmed">
              Discount
            </Text>
            <Text fw={600} c="green">
              - {sym} {Number(discountedAmount).toLocaleString("en-IN")}
            </Text>
          </Stack>
          <Stack gap={2} align="center">
            <Text size="xs" c="dimmed">
              Total Amount
            </Text>
            <Text fw={700} c="#003594">
              {sym} {Number(total).toLocaleString("en-IN")}
            </Text>
          </Stack>
        </SimpleGrid>
      </Paper>
      {isCouponApplied ? (
        <Alert color="green" icon={<IconCheck size={16} />} mt="sm" py="xs">
          Coupon applied successfully!
        </Alert>
      ) : null}
    </>
  );
};

const StepTwo = ({
  quoteData,
  filteredCovers,
  coverOption,
  onCoverChange,
  couponCode,
  setCouponCode,
  applyCoupon,
  isCouponApplying,
  isCouponApplied,
  originalAmount,
  discountedAmount,
}) => {
  const sym = quoteData?.currency_symbol || "₹";
  const couponUi = coverOption ? (
    <>
      <CouponBlock
        couponCode={couponCode}
        setCouponCode={setCouponCode}
        applyCoupon={applyCoupon}
        isCouponApplying={isCouponApplying}
        isCouponApplied={isCouponApplied}
      />
      {originalAmount !== null && discountedAmount !== null ? (
        <PriceSummary
          sym={sym}
          originalAmount={originalAmount}
          discountedAmount={discountedAmount}
          isCouponApplied={isCouponApplied}
        />
      ) : null}
    </>
  ) : null;

  if (quoteData?.plan) {
    return (
      <>
        <CoverPlanOptions
          plan={quoteData.plan}
          coverOption={coverOption}
          onCoverChange={onCoverChange}
        />
        {couponUi}
      </>
    );
  }

  if (filteredCovers.length === 0) {
    return <p>No covers available.</p>;
  }

  return (
    <>
      <Radio.Group
        value={coverOption}
        onChange={onCoverChange}
        name="coverSelection"
        label="Select your cover"
        withAsterisk
        color="brand"
      >
        <Group mt="md">
          {filteredCovers.map((cover, index) => (
            <Radio
              key={index}
              value={cover.type}
              label={<PriceCard cover={cover} currency={cover.currency} index={index} />}
              disabled={filteredCovers.length === 1}
            />
          ))}
        </Group>
      </Radio.Group>
      {couponUi}
    </>
  );
};

export default StepTwo;
